import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { X, MapPin, Calendar, Home, Users, Phone, Mail, Copy, Shield, User, CheckCircle, Scale } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import ImageViewer from './ImageViewer';
import PropertyAmenitiesDisplay from './property/PropertyAmenitiesDisplay';
import ListingDetailActions from './ListingDetailActions';

interface ListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLang: string;
  onAddToComparison?: (property: any) => void;
  listing: {
    id: string;
    title: string;
    titleAr: string;
    price: number;
    deposit: number;
    university: string;
    distance: number;
    furnished: boolean;
    verified: boolean;
    images: string[];
    unitType: 'room' | 'shared' | 'entire';
    genderRules: 'mixed' | 'female' | 'male';
    responseRate: number;
    ownerName: string;
    description?: string;
    address?: string;
    amenities?: string[];
    availableFrom?: string;
    leaseMinMonths?: number;
    roommatesAllowed?: boolean;
    floorPlanUrl?: string;
    videoUrl?: string;
    whatsapp_link?: string;
    google_maps_link?: string;
  };
}

const ListingModal = ({
  isOpen,
  onClose,
  listing,
  currentLang,
  onAddToComparison
}: ListingModalProps) => {
  const { t } = useLanguage();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const getUnitTypeLabel = (type: string) => {
    const labels = {
      room: {
        en: 'Private Room',
        ar: 'غرفة خاصة'
      },
      shared: {
        en: 'Shared Room',
        ar: 'غرفة مشتركة'
      },
      entire: {
        en: 'Entire Unit',
        ar: 'وحدة كاملة'
      }
    };
    return currentLang === 'ar' ? labels[type as keyof typeof labels]?.ar : labels[type as keyof typeof labels]?.en;
  };

  const getGenderLabel = (gender: string) => {
    const labels = {
      mixed: {
        en: 'Mixed',
        ar: 'مختلط'
      },
      female: {
        en: 'Female Only',
        ar: 'إناث فقط'
      },
      male: {
        en: 'Male Only',
        ar: 'ذكور فقط'
      }
    };
    return currentLang === 'ar' ? labels[gender as keyof typeof labels]?.ar : labels[gender as keyof typeof labels]?.en;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  const openImageViewer = (index: number) => {
    setSelectedImageIndex(index);
    setShowImageViewer(true);
  };

  const handleWishlistToggle = () => {
    setIsWishlisted(!isWishlisted);
  };

  const handleScheduleVisit = () => {
    toast.info(t('visit_scheduling_coming_soon', 'ميزة جدولة الزيارة قريباً', 'Visit scheduling feature coming soon'));
  };

  const handleOpenChat = () => {
    toast.info(t('chat_feature_coming_soon', 'ميزة المحادثة قريباً', 'Chat feature coming soon'));
  };

  const handleAddToComparison = () => {
    if (onAddToComparison) {
      const propertyForComparison = {
        id: listing.id,
        title: listing.title,
        price_egp: listing.price,
        deposit_egp: listing.deposit,
        distance_km: listing.distance,
        unit_type: listing.unitType,
        furnished: listing.furnished,
        verified: listing.verified,
        photos: listing.images,
        amenities: listing.amenities || [],
        available_from: listing.availableFrom || new Date().toISOString(),
        lease_min_months: listing.leaseMinMonths || 12,
        gender_rules: listing.genderRules,
        universities: {
          name_en: listing.university,
          name_ar: listing.university
        }
      };
      onAddToComparison(propertyForComparison);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${currentLang === 'ar' ? 'rtl' : 'ltr'}`}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {currentLang === 'ar' ? listing.titleAr : listing.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {listing.images.map((image, index) => (
                  <div
                    key={index}
                    className="aspect-video bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openImageViewer(index)}
                  >
                    <img
                      src={image}
                      alt={`${listing.title} - Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Owner Details Section */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{listing.ownerName}</h3>
                      <p className="text-sm text-muted-foreground">
                        {currentLang === 'ar' ? 'مالك العقار' : 'Property Owner'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {listing.verified && (
                      <Badge variant="secondary" className="bg-green-100 text-green-800 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        {currentLang === 'ar' ? 'موثق' : 'Verified'}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Property Tags */}
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-sm">
                {getUnitTypeLabel(listing.unitType)}
              </Badge>
              <Badge variant="outline" className="text-sm">
                {getGenderLabel(listing.genderRules)}
              </Badge>
              {listing.furnished && (
                <Badge variant="secondary" className="text-sm bg-green-100 text-green-800">
                  <Home className="h-3 w-3 mr-1" />
                  {currentLang === 'ar' ? 'مفروش' : 'Furnished'}
                </Badge>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="md:col-span-2 space-y-6">
                {/* Price */}
                <div className="flex flex-col items-start">
                  <div className="text-3xl font-bold text-primary">
                    {listing.price.toLocaleString()} {currentLang === 'ar' ? 'ج.م' : 'EGP'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {currentLang === 'ar' ? '/شهر' : '/month'}
                  </div>
                  {listing.deposit > 0 && (
                    <div className="text-sm text-muted-foreground mt-1">
                      {currentLang === 'ar' ? 'تأمين:' : 'Deposit:'} {listing.deposit.toLocaleString()} {currentLang === 'ar' ? 'ج.م' : 'EGP'}
                    </div>
                  )}
                </div>

                {/* Location */}
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>{listing.distance}km {currentLang === 'ar' ? 'من' : 'from'} {listing.university}</span>
                </div>

                {/* Description */}
                {listing.description && (
                  <div>
                    <h3 className="font-semibold mb-2">
                      {currentLang === 'ar' ? 'الوصف' : 'Description'}
                    </h3>
                    <p className="text-muted-foreground">{listing.description}</p>
                  </div>
                )}

                {/* Property Details */}
                <div>
                  <h3 className="font-semibold mb-4">
                    {currentLang === 'ar' ? 'تفاصيل العقار' : 'Property Details'}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {listing.availableFrom && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        <span className="text-sm">
                          <strong>{currentLang === 'ar' ? 'متاح من:' : 'Available from:'}</strong>{' '}
                          {new Date(listing.availableFrom).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {listing.leaseMinMonths && (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        <span className="text-sm">
                          <strong>{currentLang === 'ar' ? 'أقل مدة إيجار:' : 'Minimum lease:'}</strong>{' '}
                          {listing.leaseMinMonths} {currentLang === 'ar' ? 'شهور' : 'months'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-primary" />
                      <span className="text-sm">
                        <strong>{currentLang === 'ar' ? 'مفروش:' : 'Furnished:'}</strong>{' '}
                        {listing.furnished ? (currentLang === 'ar' ? 'نعم' : 'Yes') : (currentLang === 'ar' ? 'لا' : 'No')}
                      </span>
                    </div>
                    {listing.roommatesAllowed !== undefined && (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        <span className="text-sm">
                          <strong>{currentLang === 'ar' ? 'زملاء السكن مسموح:' : 'Roommates allowed:'}</strong>{' '}
                          {listing.roommatesAllowed ? (currentLang === 'ar' ? 'نعم' : 'Yes') : (currentLang === 'ar' ? 'لا' : 'No')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Amenities */}
                {listing.amenities && listing.amenities.length > 0 && (
                  <PropertyAmenitiesDisplay amenities={listing.amenities} />
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                {/* Contact Actions */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-4">
                      {currentLang === 'ar' ? 'تواصل مع المالك' : 'Contact Owner'}
                    </h3>
                    <ListingDetailActions
                      listing={listing}
                      isInWishlist={isWishlisted}
                      onToggleWishlist={handleWishlistToggle}
                      onScheduleVisit={handleScheduleVisit}
                      onOpenChat={handleOpenChat}
                      onAddToComparison={onAddToComparison}
                    />
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {currentLang === 'ar' ? 'المسافة:' : 'Distance:'}
                      </span>
                      <span className="text-sm font-medium">
                        {listing.distance}km
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {currentLang === 'ar' ? 'معدل الاستجابة:' : 'Response Rate:'}
                      </span>
                      <span className="text-sm font-medium">
                        {listing.responseRate}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {currentLang === 'ar' ? 'متاح من:' : 'Available For:'}
                      </span>
                      <span className="text-sm font-medium">
                        {currentLang === 'ar' ? 'أغسطس 15، 2024' : 'August 15, 2024'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        {currentLang === 'ar' ? 'أقل مدة إيجار:' : 'Minimum Lease:'}
                      </span>
                      <span className="text-sm font-medium">
                        {listing.leaseMinMonths || 5} {currentLang === 'ar' ? 'شهور' : 'months'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Viewer */}
      {showImageViewer && (
        <ImageViewer
          isOpen={showImageViewer}
          images={listing.images}
          initialIndex={selectedImageIndex}
          onClose={() => setShowImageViewer(false)}
        />
      )}
    </>
  );
};

export default ListingModal;
